
import { safeGet, safeSet, safeRemove } from "./safe-storage";

const MAX_NAG_COUNT = 3;

interface TourNagState {
  skipCount: number;
  bannerDismissCount: number;
  lastSkipAt?: string;
  lastBannerDismissedAt?: string;
}

const getStorageKey = (userId?: string) =>
  userId ? `upsider_tour_nag:${userId}` : "upsider_tour_nag";

function parseState(raw: string | null): TourNagState {
  if (!raw) {
    return { skipCount: 0, bannerDismissCount: 0 };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      skipCount: typeof parsed.skipCount === "number" ? parsed.skipCount : 0,
      bannerDismissCount: typeof parsed.bannerDismissCount === "number" ? parsed.bannerDismissCount : 0,
      lastSkipAt: parsed.lastSkipAt,
      lastBannerDismissedAt: parsed.lastBannerDismissedAt,
    };
  } catch {
    return { skipCount: 0, bannerDismissCount: 0 };
  }
}

export function getTourNagState(userId?: string): TourNagState {
  const key = getStorageKey(userId);
  return parseState(safeGet(key));
}

export function incrementTourSkip(userId?: string): TourNagState {
  const key = getStorageKey(userId);
  const state = getTourNagState(userId);
  const newState: TourNagState = {
    ...state,
    skipCount: state.skipCount + 1,
    lastSkipAt: new Date().toISOString(),
  };
  safeSet(key, JSON.stringify(newState));
  return newState;
}

export function incrementBannerDismiss(userId?: string): TourNagState {
  const key = getStorageKey(userId);
  const state = getTourNagState(userId);
  const newState: TourNagState = {
    ...state,
    bannerDismissCount: state.bannerDismissCount + 1,
    lastBannerDismissedAt: new Date().toISOString(),
  };
  safeSet(key, JSON.stringify(newState));
  return newState;
}

export function resetTourNagState(userId?: string): void {
  const key = getStorageKey(userId);
  safeRemove(key);
}

export function shouldShowReminderBanner(
  userId?: string,
  sessionStartTime?: number
): boolean {
  const state = getTourNagState(userId);

  if (state.skipCount === 0) return false;

  if (state.bannerDismissCount >= MAX_NAG_COUNT) return false;

  if (sessionStartTime && state.lastSkipAt) {
    const skipTime = new Date(state.lastSkipAt).getTime();
    if (skipTime >= sessionStartTime) {
      return false;
    }
  }

  return true;
}

export function shouldShowWelcomeModal(userId?: string): boolean {
  const state = getTourNagState(userId);
  return state.skipCount === 0;
}

export { MAX_NAG_COUNT };
